import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Divider,
  Grid,
  
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Switch,
  FormControlLabel,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Visibility,
  VisibilityOff,
  AccountBalance as BalanceIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  balanceCard: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
  },
  balanceValue: {
    fontSize: "2.5rem",
    fontWeight: 700,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  helpText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  limitCard: {
    marginBottom: theme.spacing(2),
    border: "1px solid #e0e0e0",
  },
  limitCardEnabled: {
    border: `2px solid ${theme.palette.success.main}`,
    backgroundColor: "#f1f8f4",
  },
  limitCardDisabled: {
    border: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    opacity: 0.7,
  },
}));

const SmsSettings = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  const [provider, setProvider] = useState("comtele");
  const [apiKey, setApiKey] = useState("");
  const [apiUrl, setApiUrl] = useState("https://sms.comtele.com.br/api/v2");
  const [sender, setSender] = useState("CenterChat");
  const [balance, setBalance] = useState(null);

  // Definição dos provedores disponíveis
  const providers = [
    { 
      value: "comtele", 
      label: "Comtele", 
      description: "Provedor brasileiro padrão",
      hasApiUrl: true,
      defaultUrl: "https://sms.comtele.com.br/api/v2",
      apiKeyFormat: "XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX",
      apiKeyLabel: "Chave de API",
      helpUrl: "https://sms.comtele.com.br",
      docsUrl: "https://docs.comtele.com.br"
    },
    { 
      value: "zenvia", 
      label: "Zenvia", 
      description: "Líder em SMS corporativo no Brasil",
      hasApiUrl: false,
      defaultUrl: "https://api.zenvia.com/v2",
      apiKeyFormat: "Token de API",
      apiKeyLabel: "X-API-TOKEN",
      helpUrl: "https://app.zenvia.com",
      docsUrl: "https://zenvia.github.io/zenvia-openapi-spec"
    },
    { 
      value: "tww", 
      label: "TWW (Total IP)", 
      description: "Provedor nacional com bons preços",
      hasApiUrl: false,
      defaultUrl: "https://api-rest.zenvia.com",
      apiKeyFormat: "NumUsu:Senha (ex: 12345:minhasenha)",
      apiKeyLabel: "Usuário:Senha",
      helpUrl: "https://www.twwsms.com.br",
      docsUrl: "https://www.twwsms.com.br/api"
    },
    { 
      value: "infobip", 
      label: "Infobip", 
      description: "Plataforma global de mensagens",
      hasApiUrl: true,
      defaultUrl: "https://api.infobip.com",
      apiKeyFormat: "App Key",
      apiKeyLabel: "App Key",
      helpUrl: "https://portal.infobip.com",
      docsUrl: "https://www.infobip.com/docs/api"
    },
    { 
      value: "pontaltech", 
      label: "PontalTech", 
      description: "Solução brasileira com API simples",
      hasApiUrl: false,
      defaultUrl: "https://api.pontaltech.com.br/v1",
      apiKeyFormat: "Bearer Token",
      apiKeyLabel: "Token de API",
      helpUrl: "https://pontaltech.com.br",
      docsUrl: "https://pontaltech.com.br/api-docs"
    },
  ];

  const currentProvider = providers.find(p => p.value === provider) || providers[0];

  // Limites de SMS
  const [limit160, setLimit160] = useState(true);
  const [limit306, setLimit306] = useState(false);
  const [limit459, setLimit459] = useState(false);
  const [limit612, setLimit612] = useState(false);

  useEffect(() => {
    loadSettings();
    loadBalance();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/settings");
      
      // Novos campos genéricos multi-provider
      const providerSetting = data.find(s => s.key === "smsProvider");
      const apiKeySetting = data.find(s => s.key === "smsApiKey");
      const apiUrlSetting = data.find(s => s.key === "smsApiUrl");
      const senderSetting = data.find(s => s.key === "smsSender");
      
      if (providerSetting) {
        const providerValue = providerSetting.value || "comtele";
        setProvider(providerValue);
        // Define URL padrão baseado no provedor
        const providerConfig = providers.find(p => p.value === providerValue);
        if (providerConfig) {
          setApiUrl(apiUrlSetting?.value || providerConfig.defaultUrl);
        }
      }
      if (apiKeySetting) setApiKey(apiKeySetting.value || "");
      if (senderSetting) setSender(senderSetting.value || "CenterChat");

      // Carrega limites de SMS
      const limit160Setting = data.find(s => s.key === "smsLimit160");
      const limit306Setting = data.find(s => s.key === "smsLimit306");
      const limit459Setting = data.find(s => s.key === "smsLimit459");
      const limit612Setting = data.find(s => s.key === "smsLimit612");

      // Limite de 160 sempre ativo
      setLimit160(true);
      if (limit306Setting) setLimit306(limit306Setting.value === "enabled");
      if (limit459Setting) setLimit459(limit459Setting.value === "enabled");
      if (limit612Setting) setLimit612(limit612Setting.value === "enabled");
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const loadBalance = async () => {
    try {
      setLoadingBalance(true);
      const { data } = await api.get("/sms/balance");
      setBalance(data.balance);
    } catch (error) {
      console.log("SMS não configurado ou erro ao buscar saldo");
      setBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error("A chave de API é obrigatória");
      return;
    }

    try {
      setSaving(true);

      // Salva configurações genéricas multi-provider
      await api.put("/settings/smsProvider", { value: provider });
      await api.put("/settings/smsApiKey", { value: apiKey });
      await api.put("/settings/smsApiUrl", { value: apiUrl });
      await api.put("/settings/smsSender", { value: sender });
      
      // Garantir que o limite de 160 sempre esteja ativo
      await api.put("/settings/smsLimit160", { value: "enabled" });

      toast.success("Configurações salvas com sucesso!");
      loadBalance(); // Recarrega o saldo após salvar
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      toast.error(error.response?.data?.message || "Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLimit = async (limitKey, currentValue) => {
    // Impede desativação do limite de 160 (sempre ativo)
    if (limitKey === "smsLimit160") {
      toast.info("O limite de 160 caracteres está sempre ativo e não pode ser desativado");
      return;
    }

    // Valida cascata ao habilitar
    if (!currentValue) {
      if (limitKey === "smsLimit306" && !limit160) {
        toast.warning("Ative o limite de 160 caracteres primeiro");
        return;
      }
      if (limitKey === "smsLimit459" && !limit306) {
        toast.warning("Ative o limite de 306 caracteres primeiro");
        return;
      }
      if (limitKey === "smsLimit612" && !limit459) {
        toast.warning("Ative o limite de 459 caracteres primeiro");
        return;
      }
    }

    // Desabilita cascata ao desativar
    const newValue = !currentValue;
    try {
      setLoading(true);
      
      await api.put(`/settings/${limitKey}`, { 
        value: newValue ? "enabled" : "disabled" 
      });

      // Atualiza estado local
      if (limitKey === "smsLimit306") {
        setLimit306(newValue);
        if (!newValue) {
          // Desativa 459 e 612 em cascata
          if (limit459) {
            await api.put(`/settings/smsLimit459`, { value: "disabled" });
            setLimit459(false);
          }
          if (limit612) {
            await api.put(`/settings/smsLimit612`, { value: "disabled" });
            setLimit612(false);
          }
        }
      } else if (limitKey === "smsLimit459") {
        setLimit459(newValue);
        if (!newValue && limit612) {
          // Desativa 612 em cascata
          await api.put(`/settings/smsLimit612`, { value: "disabled" });
          setLimit612(false);
        }
      } else if (limitKey === "smsLimit612") {
        setLimit612(newValue);
      }

      if (limitKey !== "smsLimit160") {
        toast.success("Limite atualizado com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao atualizar limite:", error);
      toast.error("Erro ao atualizar limite");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper className={classes.root}>
      <Typography variant="h4" className={classes.title}>
        Configurações SMS
      </Typography>

      {/* Saldo */}
      <Box className={classes.section}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.balanceCard}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Saldo Disponível
                    </Typography>
                    {loadingBalance ? (
                      <CircularProgress size={40} style={{ color: "white" }} />
                    ) : (
                      <Typography className={classes.balanceValue}>
                        {balance !== null ? `${balance} créditos` : "—"}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    onClick={loadBalance}
                    style={{ color: "white" }}
                    disabled={loadingBalance}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {/* Configuração da API */}
      <Box className={classes.section} mt={3}>
        <Typography variant="h6" gutterBottom>
          Integração SMS - Multi-Provedor
        </Typography>

        <Grid container spacing={2}>
          {/* Seletor de Provedor */}
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Provedor SMS</InputLabel>
              <Select
                value={provider}
                onChange={(e) => {
                  const newProvider = e.target.value;
                  setProvider(newProvider);
                  const providerConfig = providers.find(p => p.value === newProvider);
                  if (providerConfig) {
                    setApiUrl(providerConfig.defaultUrl);
                  }
                }}
                label="Provedor SMS"
              >
                {providers.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    <Box>
                      <Typography variant="body1">{p.label}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {p.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Escolha o provedor de SMS que deseja utilizar
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Nome do Remetente */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome do Remetente"
              variant="outlined"
              fullWidth
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              helperText="Nome que aparecerá como remetente do SMS"
            />
          </Grid>

          {/* Chave de API */}
          <Grid item xs={12}>
            <TextField
              label={currentProvider.apiKeyLabel}
              type={showApiKey ? "text" : "password"}
              variant="outlined"
              fullWidth
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowApiKey(!showApiKey)}
                      edge="end"
                    >
                      {showApiKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText={`Formato: ${currentProvider.apiKeyFormat}`}
            />
          </Grid>

          {/* URL da API (apenas para provedores configuráveis) */}
          {currentProvider.hasApiUrl && (
            <Grid item xs={12}>
              <TextField
                label="URL da API"
                variant="outlined"
                fullWidth
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                helperText={`URL base da API ${currentProvider.label} ${provider === 'infobip' ? '(varia por região)' : '(geralmente não precisa alterar)'}`}
              />
            </Grid>
          )}
        </Grid>

        <Typography variant="body2" className={classes.helpText}>
          <strong>Como obter sua chave de API:</strong>
          <ol>
            <li>
              Acesse:{" "}
              <a
                href={currentProvider.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                {currentProvider.helpUrl}
              </a>
            </li>
            {provider === "comtele" && (
              <>
                <li>Vá em: Menu Lateral &gt; API &gt; Chave de API</li>
                <li>Copie a chave e cole no campo acima</li>
              </>
            )}
            {provider === "zenvia" && (
              <>
                <li>Vá em: Configurações &gt; API</li>
                <li>Gere um novo token de API e copie</li>
              </>
            )}
            {provider === "tww" && (
              <>
                <li>Obtenha seu Número de Usuário e Senha</li>
                <li>Formate como: NumeroUsuario:Senha (ex: 12345:minhasenha)</li>
              </>
            )}
            {provider === "infobip" && (
              <>
                <li>Vá em: Settings &gt; API Keys</li>
                <li>Crie uma App Key e copie</li>
                <li>Configure a URL base da sua região (ex: https://xyz123.api.infobip.com)</li>
              </>
            )}
            {provider === "pontaltech" && (
              <>
                <li>Acesse o painel de controle</li>
                <li>Gere um token de API Bearer</li>
              </>
            )}
          </ol>
        </Typography>

        <Typography variant="body2" className={classes.helpText}>
          📚 Documentação oficial:{" "}
          <a
            href={currentProvider.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.link}
          >
            {currentProvider.docsUrl}
          </a>
        </Typography>

        <Box mt={3}>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Salvando..." : "Salvar Configurações"}
          </Button>
        </Box>
      </Box>

      <Divider />

      {/* Limites de Caracteres */}
      <Box className={classes.section} mt={3}>
        <Typography variant="h6" gutterBottom>
          Limites de Caracteres por SMS
        </Typography>
        
        <Typography variant="body2" className={classes.helpText} gutterBottom>
          Controle quais limites de mensagem estão disponíveis. Os limites devem ser ativados em ordem.
        </Typography>

        <Grid container spacing={2} style={{ marginTop: 8 }}>
          {/* 160 caracteres */}
          <Grid item xs={12} sm={6} md={3}>
            <Card className={limit160 ? classes.limitCardEnabled : classes.limitCardDisabled}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex={1}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                      Até 160 chars
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 4 }}>
                      1 crédito
                    </Typography>
                    <Tooltip title="O limite de 160 caracteres está sempre ativo">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={true}
                            onChange={() => handleToggleLimit("smsLimit160", limit160)}
                            disabled={true}
                            color="primary"
                          />
                        }
                        label="Sempre Ativo"
                      />
                    </Tooltip>
                  </Box>
                  <Tooltip title="Sempre ativo">
                    <LockOpenIcon style={{ color: "#4caf50" }} />
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 161-306 caracteres */}
          <Grid item xs={12} sm={6} md={3}>
            <Card className={limit306 ? classes.limitCardEnabled : classes.limitCardDisabled}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex={1}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                      161-306 chars
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 4 }}>
                      2 créditos (153 cada)
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={limit306}
                          onChange={() => handleToggleLimit("smsLimit306", limit306)}
                          disabled={!limit160 || loading}
                          color="primary"
                        />
                      }
                      label={limit306 ? "Ativo" : "Inativo"}
                    />
                  </Box>
                  {!limit160 ? (
                    <Tooltip title="Ative o limite anterior">
                      <LockIcon style={{ color: "#999" }} />
                    </Tooltip>
                  ) : (
                    limit306 ? <LockOpenIcon style={{ color: "#4caf50" }} /> : <LockIcon style={{ color: "#999" }} />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 307-459 caracteres */}
          <Grid item xs={12} sm={6} md={3}>
            <Card className={limit459 ? classes.limitCardEnabled : classes.limitCardDisabled}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex={1}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                      307-459 chars
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 4 }}>
                      3 créditos
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={limit459}
                          onChange={() => handleToggleLimit("smsLimit459", limit459)}
                          disabled={!limit306 || loading}
                          color="primary"
                        />
                      }
                      label={limit459 ? "Ativo" : "Inativo"}
                    />
                  </Box>
                  {!limit306 ? (
                    <Tooltip title="Ative o limite anterior">
                      <LockIcon style={{ color: "#999" }} />
                    </Tooltip>
                  ) : (
                    limit459 ? <LockOpenIcon style={{ color: "#4caf50" }} /> : <LockIcon style={{ color: "#999" }} />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 460-612 caracteres */}
          <Grid item xs={12} sm={6} md={3}>
            <Card className={limit612 ? classes.limitCardEnabled : classes.limitCardDisabled}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex={1}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                      460-612 chars
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 4 }}>
                      4 créditos
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={limit612}
                          onChange={() => handleToggleLimit("smsLimit612", limit612)}
                          disabled={!limit459 || loading}
                          color="primary"
                        />
                      }
                      label={limit612 ? "Ativo" : "Inativo"}
                    />
                  </Box>
                  {!limit459 ? (
                    <Tooltip title="Ative o limite anterior">
                      <LockIcon style={{ color: "#999" }} />
                    </Tooltip>
                  ) : (
                    limit612 ? <LockOpenIcon style={{ color: "#4caf50" }} /> : <LockIcon style={{ color: "#999" }} />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="body2" className={classes.helpText} style={{ marginTop: 16 }}>
          ⚠️ <strong>Importante:</strong> Ao desativar um limite, todos os limites superiores serão desativados automaticamente.
        </Typography>
        
        <Typography variant="body2" style={{ marginTop: 8, padding: 12, backgroundColor: "#e8f5e9", borderRadius: 4, border: "1px solid #4caf50" }}>
          ✅ <strong>Limite Básico:</strong> O limite de 160 caracteres está sempre ativo. Você pode ativar limites superiores conforme necessário.
        </Typography>
      </Box>
    </Paper>
  );
};

export default SmsSettings;
